import React, { useState } from 'react';
import { ControllableElement, compareByRenderOrderDesc } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import { notify } from '@bynder/design-system';
import ComponentModal from './ComponentModal';
import { ComponentModalMode } from './types';
import useComponents from '../useComponents';
import ComponentService, { AddComponentRequest, PartialComponent } from 'packages/services/ComponentService';
import ComponentCollectionService from 'packages/services/ComponentCollectionService';

interface UseComponentModalProps {
    onSaveSuccess?: (resetCollection: boolean) => void;
}

const useComponentModal = ({ onSaveSuccess }: UseComponentModalProps) => {
    const { translate } = useTranslate();
    const [isOpen, setOpen] = useState(false);
    const [mode, setMode] = useState<ComponentModalMode>('create');
    const [selectedElements, setSelectedElements] = useState<ControllableElement[]>([]);
    const [component, setComponent] = useState<PartialComponent | null>(null);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const { createComponent } = useComponents();

    const onOpen = (
        modeProp: ComponentModalMode,
        selectedElements: ControllableElement[],
        component?: PartialComponent,
        selectedCollectionId?: number,
    ) => {
        if (isOpen) {
            return;
        }

        if (selectedElements.length === 0 && modeProp === 'create') {
            return;
        }

        setMode(modeProp);

        if (selectedElements.length > 0) {
            setSelectedElements(selectedElements);
        }

        if (selectedCollectionId) {
            setSelectedCollectionId(selectedCollectionId);
        }

        if (modeProp === 'edit' && component) {
            setComponent(component);
        }

        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setSelectedElements([]);
    };

    const onSave = async ({
        name,
        description,
        collectionId,
        collectionName,
        id,
    }: {
        name: string;
        collectionId: number | null;
        description?: string;
        id?: number;
        collectionName?: string;
    }) => {
        setIsSaving(true);
        const finalCollectionId = await resolveCollectionId(collectionId, collectionName);

        if (finalCollectionId === null) {
            setIsSaving(false);
            return;
        }

        const res =
            mode === 'create'
                ? await createNewComponent(name, description, finalCollectionId)
                : await editExistingComponent(id, name, description, finalCollectionId);

        handleSaveResponse(res);

        setOpen(false);

        // Clear selected elements after a delay to allow the modal to close smoothly
        setTimeout(() => {
            setSelectedElements([]);
            setIsSaving(false);
        }, 300);
    };

    const resolveCollectionId = async (
        collectionId: number | null,
        collectionName?: string,
    ): Promise<number | null> => {
        if (collectionName) {
            const res = await ComponentCollectionService.addComponentCollection({
                name: collectionName,
            });

            return res.status === 200 ? res.json.id : null;
        }

        return collectionId ? Number(collectionId) : null;
    };

    const createNewComponent = async (name: string, description: string, collectionId: number) => {
        const preparedComponent = createComponent(selectedElements);

        const mergedComponent: AddComponentRequest = {
            name,
            description: description || undefined,
            collectionId,
            elements: preparedComponent.elements,
            textStyles: preparedComponent.textStyles || [],
            contentProperties: preparedComponent.contentProperties,
            type: preparedComponent.type,
        };

        return ComponentService.addComponent(mergedComponent);
    };

    const editExistingComponent = async (
        id: number | undefined,
        name: string,
        description: string,
        collectionId: number,
    ) => {
        const payload: { name: string; collectionId: number; description?: string } = {
            name,
            collectionId,
        };

        if (description) {
            payload.description = description;
        }

        return ComponentService.editComponent(id, payload);
    };

    const handleSaveResponse = (res: { status: number }) => {
        if (res.status === 200) {
            notify({
                title: translate('components.create.modal.save.success'),
                variant: 'success',
            });

            onSaveSuccess?.(!selectedCollectionId);
        } else {
            notify({
                title: translate('components.create.modal.save.error.badRequest'),
                variant: 'error',
            });
        }
    };

    const renderComponentModal = () => {
        const suggestedName =
            selectedElements.length > 0
                ? [...selectedElements].sort(compareByRenderOrderDesc)[0].name
                : 'New component';

        return (
            <ComponentModal
                isOpen={isOpen}
                onClose={onClose}
                onSave={onSave}
                mode={mode}
                suggestedName={suggestedName}
                isSaving={isSaving}
                component={component}
            />
        );
    };

    return {
        openComponentModal: onOpen,
        renderComponentModal,
    };
};

export default useComponentModal;
